import * as THREE from 'three';
import { countGeometryStats } from '../core/math';
import { createStarbeamMaterial, createStoneMaterial } from '../shaders/materials';
import type { InteractiveElement, SpecimenInstance, TemporalUniforms } from '../core/types';

export interface TelescopeResult {
  group: THREE.Group;
  barrelPivot: THREE.Group;
  interactables: InteractiveElement[];
  update: (elapsed: number, temporal: number) => void;
}

/**
 * Builds a Keplerian brass & crystal observatory telescope mounted inside
 * an engraved armillary astrolabe cradle, projecting a volumetric starbeam cone.
 */
export function createBrassTelescope(
  uniforms: TemporalUniforms,
  scale = 1.0,
  azimuthOffset = 0,
  includeStarbeam = true
): TelescopeResult {
  const group = new THREE.Group();
  const interactables: InteractiveElement[] = [];

  const polishedBrassMat = new THREE.MeshStandardMaterial({
    color: 0xe5b25d,
    roughness: 0.22,
    metalness: 0.88,
  });

  const antiqueBronzeMat = new THREE.MeshStandardMaterial({
    color: 0x6e5239,
    roughness: 0.42,
    metalness: 0.78,
  });

  const lensMat = new THREE.MeshPhysicalMaterial({
    color: 0x8de0ff,
    emissive: 0x3b9eff,
    emissiveIntensity: 0.65,
    roughness: 0.05,
    metalness: 0.1,
    transmission: 0.75,
    transparent: true,
    opacity: 0.9,
  });

  const pedestalStoneMat = createStoneMaterial(uniforms, {
    baseDesireHex: 0x4b333c,
    baseMemoryHex: 0x202b42,
    roughness: 0.6,
    metalness: 0.2,
  });

  // 1. Stone & Brass Azimuthal Observatory Pedestal
  const baseGeo = new THREE.CylinderGeometry(0.75, 1.05, 0.9, 24);
  const baseMesh = new THREE.Mesh(baseGeo, pedestalStoneMat);
  baseMesh.position.y = 0.45;
  baseMesh.castShadow = true;
  group.add(baseMesh);

  // Azimuth degree ring
  const azRingGeo = new THREE.TorusGeometry(0.85, 0.045, 12, 48);
  azRingGeo.rotateX(Math.PI / 2);
  const azRing = new THREE.Mesh(azRingGeo, polishedBrassMat);
  azRing.position.y = 0.92;
  group.add(azRing);

  // Equatorial / Alt-Azimuth Yoke & Armillary Meridian Rings
  const yokeGroup = new THREE.Group();
  yokeGroup.position.y = 1.55;
  yokeGroup.rotation.y = azimuthOffset;
  group.add(yokeGroup);

  const meridianRingGeo = new THREE.TorusGeometry(0.95, 0.038, 12, 64);
  const meridianRing = new THREE.Mesh(meridianRingGeo, polishedBrassMat);
  yokeGroup.add(meridianRing);

  const horizonRingGeo = new THREE.TorusGeometry(0.88, 0.028, 10, 48);
  horizonRingGeo.rotateX(Math.PI / 2);
  const horizonRing = new THREE.Mesh(horizonRingGeo, antiqueBronzeMat);
  yokeGroup.add(horizonRing);

  // 2. Stepped Keplerian Brass Telescope Tube
  const barrelPivot = new THREE.Group();
  barrelPivot.rotation.x = -Math.PI * 0.24; // Aimed 43 degrees up into the twilight stars
  yokeGroup.add(barrelPivot);

  const tubeSegments = [
    { rTop: 0.26, rBot: 0.24, len: 1.15, z: 0.65, mat: polishedBrassMat },
    { rTop: 0.21, rBot: 0.23, len: 1.05, z: -0.35, mat: antiqueBronzeMat },
    { rTop: 0.14, rBot: 0.19, len: 0.65, z: -1.1, mat: polishedBrassMat },
  ];

  for (const seg of tubeSegments) {
    const tGeo = new THREE.CylinderGeometry(seg.rTop, seg.rBot, seg.len, 28);
    tGeo.rotateX(Math.PI / 2);
    const tMesh = new THREE.Mesh(tGeo, seg.mat);
    tMesh.position.z = seg.z;
    tMesh.castShadow = true;
    barrelPivot.add(tMesh);

    // Knurled brass collar ring
    const collarGeo = new THREE.TorusGeometry(seg.rTop * 1.05, 0.026, 10, 32);
    const collar = new THREE.Mesh(collarGeo, polishedBrassMat);
    collar.position.z = seg.z + seg.len * 0.48;
    barrelPivot.add(collar);
  }

  // Parallel Brass Finder Scope & Counterweight Rod
  const finderGeo = new THREE.CylinderGeometry(0.05, 0.045, 1.2, 16);
  finderGeo.rotateX(Math.PI / 2);
  const finder = new THREE.Mesh(finderGeo, polishedBrassMat);
  finder.position.set(0, 0.34, 0.15);
  barrelPivot.add(finder);

  // Objective & Eyepiece Crystal Lenses
  const objLensGeo = new THREE.SphereGeometry(0.245, 24, 16, 0, Math.PI * 2, 0, Math.PI * 0.35);
  objLensGeo.rotateX(Math.PI / 2);
  const objLens = new THREE.Mesh(objLensGeo, lensMat);
  objLens.position.z = 1.2;
  barrelPivot.add(objLens);

  const eyeLensGeo = new THREE.SphereGeometry(0.12, 16, 12);
  const eyeLens = new THREE.Mesh(eyeLensGeo, lensMat);
  eyeLens.position.z = -1.42;
  barrelPivot.add(eyeLens);

  // 3. Volumetric Starlight Ray Cone projecting into the heavens
  if (includeStarbeam) {
    const beamMat = createStarbeamMaterial(uniforms);
    const beamGeo = new THREE.CylinderGeometry(2.6, 0.24, 24.0, 32, 1, true);
    beamGeo.translate(0, 12.0, 0);
    beamGeo.rotateX(Math.PI / 2);
    const beamMesh = new THREE.Mesh(beamGeo, beamMat);
    beamMesh.position.z = 1.22;
    barrelPivot.add(beamMesh);
  }

  group.scale.setScalar(scale);

  interactables.push({
    object: barrelPivot,
    kind: 'telescope',
    title: 'Keplerian Brass Refractor Telescope',
    detail: 'Click to align the observatory lens with the zenith constellations and toggle eyepiece reticle focus.',
    zhTitle: '克卜勒黃銅折射望遠鏡',
    zhDetail: '點擊將觀星透鏡對準天頂星群，並切換目鏡十字準星對焦視野。',
    motif: 'telescopes',
    frequency: 523.25,
    onHover: (hovered) => {
      lensMat.emissiveIntensity = hovered ? 1.35 : 0.65;
    },
    onActivate: () => {
      uniforms.uTelescopeFocus.value =
        uniforms.uTelescopeFocus.value > 0.5 ? 0.0 : 1.0;
    },
  });

  return {
    group,
    barrelPivot,
    interactables,
    update: (elapsed, temporal) => {
      yokeGroup.rotation.y = azimuthOffset + Math.sin(elapsed * 0.22 + azimuthOffset) * 0.35;
      barrelPivot.rotation.x =
        -Math.PI * (0.22 + 0.06 * temporal) + Math.cos(elapsed * 0.31 + azimuthOffset) * 0.08;
    },
  };
}

/**
 * Builds the isolated Brass Telescope & Astrolabe Specimen for the Inspector turntable.
 */
export function buildTelescopeSpecimen(uniforms: TemporalUniforms): SpecimenInstance {
  const scope = createBrassTelescope(uniforms, 1.35, 0.4, false);
  const stats = countGeometryStats(scope.group);

  return {
    group: scope.group,
    update: (elapsed, _dt, temporal) => {
      scope.update(elapsed, temporal);
    },
    dispose: () => {},
    meta: {
      vertices: stats.vertices,
      triangles: stats.triangles,
      formula: '1/f = (n − 1)(1/R₁ − 1/R₂),  M = −f_objective / f_eyepiece',
      parameters: {
        'Optical Configuration': '3-Draw Keplerian Achromatic Refractor',
        'Mount Type': 'Armillary Alt-Azimuth Cradle',
        'Objective Aperture': '240mm Crown/Flint Doublet',
        'Tube Material': 'Turned Brass & Patinated Bronze',
      },
    },
  };
}
